import { useCallback, useEffect, useState } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { timeAgo } from "./AdminShared";

const LIMIT = 50;

const badgeColor = (action) => {
  if (action.includes("ban") || action.includes("delete") || action.includes("force_end")) return "text-rose-400 border-rose-600/50";
  if (action.includes("suspend")) return "text-amber-400 border-amber-500/50";
  if (action.includes("role") || action.includes("password")) return "text-sky-400 border-sky-500/50";
  return "text-zinc-400 border-white/15";
};

export default function AuditTab() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [actions, setActions] = useState([]);
  const [action, setAction] = useState("");
  const [q, setQ] = useState("");
  const [skip, setSkip] = useState(0);

  const load = useCallback(async (nextSkip, act, query) => {
    try {
      const { data } = await api.get("/admin/audit", {
        params: { limit: LIMIT, skip: nextSkip, action: act || undefined, q: query || undefined },
      });
      setRows(data.rows);
      setTotal(data.total);
      setActions(data.actions);
      setSkip(nextSkip);
    } catch {
      /* noop */
    }
  }, []);

  useEffect(() => { load(0, "", ""); }, [load]);

  const pickAction = (a) => {
    const next = a === action ? "" : a;
    setAction(next);
    load(0, next, q);
  };

  return (
    <div data-testid="admin-audit-tab">
      <div className="flex items-center gap-3 mb-3">
        <Input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && load(0, action, q)}
          placeholder="Search by actor / target handle…"
          className="bg-black/40 border-white/10 rounded-none py-6 max-w-md"
          data-testid="admin-audit-search"
        />
        <Button onClick={() => load(0, action, q)} className="btn-crimson rounded-none">Search</Button>
      </div>
      {actions.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-4">
          {actions.map((a) => (
            <button
              key={a}
              type="button"
              data-testid={`admin-audit-filter-${a.replace(":", "-")}`}
              onClick={() => pickAction(a)}
              aria-pressed={action === a}
              className={`text-[10px] uppercase tracking-widest border px-2 py-1 ${
                action === a ? "border-rose-500 text-rose-400" : "border-white/15 text-zinc-500 hover:text-white"
              }`}
            >
              {a}
            </button>
          ))}
        </div>
      )}
      <div className="space-y-1.5">
        {rows.map((r) => (
          <div key={r.id} data-testid={`admin-audit-row-${r.id}`} className="card-surface px-4 py-3 flex items-center gap-3 text-sm">
            <span className={`text-[10px] uppercase tracking-widest border px-1.5 py-0.5 shrink-0 ${badgeColor(r.action)}`}>
              {r.action}
            </span>
            <div className="min-w-0 flex-1">
              <span className="text-white">@{r.actor_handle || r.actor_id?.slice(0, 6)}</span>
              {r.target_handle ? (
                <span className="text-zinc-400"> → @{r.target_handle}</span>
              ) : r.target_id ? (
                <span className="text-zinc-500"> → {r.target_id.slice(0, 8)}…</span>
              ) : null}
              {r.meta && Object.keys(r.meta).length > 0 && (
                <span className="text-zinc-500 text-xs ml-2 truncate">
                  {Object.entries(r.meta).map(([k, v]) => `${k}=${String(v)}`).join(" · ")}
                </span>
              )}
            </div>
            <span className="text-xs text-zinc-500 shrink-0">{timeAgo(r.ts)} ago</span>
          </div>
        ))}
        {rows.length === 0 && (
          <div className="card-surface p-8 text-center text-sm text-zinc-500">No audit entries yet. Admin actions will appear here.</div>
        )}
      </div>
      {total > LIMIT && (
        <div className="flex items-center justify-between mt-4 text-xs text-zinc-500">
          <span>{skip + 1}–{Math.min(skip + LIMIT, total)} of {total}</span>
          <div className="flex gap-2">
            <Button
              disabled={skip === 0}
              onClick={() => load(Math.max(0, skip - LIMIT), action, q)}
              className="rounded-none bg-white/10 border border-white/15 text-white disabled:opacity-40"
              data-testid="admin-audit-prev"
            >
              Prev
            </Button>
            <Button
              disabled={skip + LIMIT >= total}
              onClick={() => load(skip + LIMIT, action, q)}
              className="rounded-none bg-white/10 border border-white/15 text-white disabled:opacity-40"
              data-testid="admin-audit-next"
            >
              Next
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
